(() => {
  "use strict";

  // One collector owns a document, including Shopify section re-renders.
  if (window.PartForThatAnalytics && window.PartForThatAnalytics.sitewide) return;

  const ENDPOINT = "https://lab.partforthat.com/api/analytics/browser-ingest";
  const VISITOR_KEY = "pft_shop_solutions_visitor_v1";
  const UNSCOPED_VISIT_KEY = "pft_storefront_visit_v2";
  const TEST_VISIT_KEY = "pft_storefront_visit_v2_test";
  const PRODUCTION_VISIT_KEY = "pft_storefront_visit_v2_production";
  const LEGACY_VISIT_KEY = "pft_shop_solutions_visit_v1";
  const CONTACT_EVENT_KEY = "pft_shop_solutions_contact_event_v1";
  const VISITOR_LIFETIME = 365 * 24 * 60 * 60 * 1000;
  const VISIT_LIFETIME = 30 * 60 * 1000;
  const READING_IDLE = 30 * 1000;
  const READING_INTERVAL = 30 * 1000;
  const MAX_ACTIVE_SECONDS = 4 * 60 * 60;
  const MAX_INTERVAL_EVENTS = 120;
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
  const PAGE_TYPES = new Set(["index", "page", "blog", "article", "product", "collection", "cart", "search", "policy"]);
  const CAMPAIGN_KEYS = new Set([
    "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_id",
    "gclid", "gbraid", "wbraid", "fbclid", "msclkid", "ttclid", "li_fat_id", "twclid", "ref",
  ]);
  const EVENT_PROPERTIES = {
    page_view: [],
    view_product: [],
    product_card_click: ["placement", "destination_path"],
    model_selected: ["selected_slug"],
    model_not_found: ["selection"],
    add_to_cart_click: ["placement", "destination_path"],
    shop_all_click: ["placement", "destination_path"],
    contact_form_submit: [],
    link_click: ["destination_path", "destination_host", "placement", "link_type"],
    article_scroll: ["percent"],
    article_engagement: ["active_seconds", "max_scroll_percent", "reason"],
    cart_view: [],
    cart_add_success: ["quantity"],
    cart_update: ["item_count"],
    checkout_click: ["placement"],
  };
  const SENSITIVE_QUERY_KEY = /(?:email|e-mail|phone|name|address|street|zip|postal|token|secret|password|auth|session|customer|checkout|order|account)/i;
  const VISITOR_COOKIE = "pft_analytics_visitor";
  const VISIT_COOKIE = "pft_analytics_visit";
  const VISIT_STARTED_COOKIE = "pft_analytics_visit_started";
  const VISIT_LANDING_COOKIE = "pft_analytics_visit_landing";
  const ENVIRONMENT_COOKIE = "pft_analytics_environment";
  const CART_LINK_KEY = "pft_analytics_cart_visit";

  function uuid() {
    try {
      if (typeof window.crypto.randomUUID === "function") return window.crypto.randomUUID().toLowerCase();
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 15) | 64;
      bytes[8] = (bytes[8] & 63) | 128;
      return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("")
        .replace(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/, "$1-$2-$3-$4-$5");
    } catch (_) {
      return "";
    }
  }

  function plainObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
  }

  function boundedString(value, length) {
    return typeof value === "string" ? value.replace(/[\u0000-\u001f\u007f]/g, "").slice(0, length) : "";
  }

  function publicPath(value) {
    if (typeof value !== "string" || value.length > 1024 || /[?#\\%\u0000-\u0020\u007f]/.test(value)) return "";
    if (value === "/" || /^\/(?:cart|search)\/?$/.test(value)) return value;
    const handle = "[a-zA-Z0-9][a-zA-Z0-9_-]*";
    const pattern = new RegExp(`^/(?:pages/${handle}|products/${handle}|policies/${handle}|blogs/${handle}(?:/${handle})?|collections/${handle}(?:/products/${handle})?)/?$`);
    if (!pattern.test(value)) return "";
    return value;
  }

  function host(value) {
    return typeof value === "string" && value.length <= 253 && /^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/i.test(value)
      ? value.toLowerCase() : "";
  }

  function sameSite(first, second) {
    const owned = new Set(["partforthat.com", "www.partforthat.com", "shop.partforthat.com"]);
    return first === second || (owned.has(first) && owned.has(second));
  }

  function parseReferrer() {
    try {
      const url = new URL(document.referrer);
      const hostname = host(url.hostname);
      if (!hostname || !/^https?:$/.test(url.protocol)) return null;
      // External paths can contain personal identifiers. Attribution needs only their host.
      return { host: hostname, path: sameSite(hostname, window.location.hostname) ? publicPath(url.pathname) : "" };
    } catch (_) {
      return null;
    }
  }

  function cleanReferrer(value) {
    if (!plainObject(value) || !host(value.host)) return null;
    return { host: host(value.host), path: sameSite(host(value.host), window.location.hostname) ? publicPath(value.path) : "" };
  }

  function campaignFrom(value) {
    const output = {};
    let total = 0;
    const entries = value instanceof URLSearchParams ? value.entries() : Object.entries(plainObject(value) ? value : {});
    for (const [key, raw] of entries) {
      if (!CAMPAIGN_KEYS.has(key) || Object.prototype.hasOwnProperty.call(output, key)) continue;
      const entry = boundedString(raw, 512);
      if (!entry || Object.keys(output).length >= 15 || total + key.length + entry.length > 4096) continue;
      output[key] = entry;
      total += key.length + entry.length;
    }
    return output;
  }

  function campaignFingerprint(value) {
    return JSON.stringify(Object.entries(value).sort(([first], [second]) => first.localeCompare(second)));
  }

  function safeQueryFrom(value) {
    const output = {};
    let total = 0;
    const entries = value instanceof URLSearchParams ? value.entries() : Object.entries(plainObject(value) ? value : {});
    for (const [rawKey, rawValue] of entries) {
      const key = boundedString(rawKey, 80);
      const entry = boundedString(rawValue, 512);
      if (!key || !/^[A-Za-z0-9_.~-]+$/.test(key) || SENSITIVE_QUERY_KEY.test(key)
        || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(entry) || Object.prototype.hasOwnProperty.call(output, key)) continue;
      if (Object.keys(output).length >= 30 || total + key.length + entry.length > 8192) break;
      output[key] = entry;
      total += key.length + entry.length;
    }
    return output;
  }

  function writeCookie(name, value, maxAge) {
    try { document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax; Secure`; }
    catch (_) { /* Cookie persistence is optional. */ }
  }

  function clearCookie(name) {
    try { document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax; Secure`; }
    catch (_) { /* Cookie persistence is optional. */ }
  }

  function productPayload(value) {
    if (!plainObject(value)) return null;
    const slug = boundedString(value.slug, 100);
    const model = boundedString(value.model, 200);
    if (!/^[a-z0-9][a-z0-9-]*$/.test(slug) || !model) return null;
    const product = { slug, model };
    const sku = boundedString(value.sku, 100);
    const variant = String(value.variant_id || "");
    if (sku) product.sku = sku;
    if (/^[0-9]{1,20}$/.test(variant)) product.variant_id = variant;
    if (typeof value.price === "number" && Number.isFinite(value.price) && value.price >= 0 && value.price <= 1000000) product.price = value.price;
    if (typeof value.currency === "string" && /^[A-Z]{3}$/.test(value.currency)) product.currency = value.currency;
    return product;
  }

  let config = {};
  try {
    const element = document.getElementById("pft-lab-config");
    const parsed = element ? JSON.parse(element.textContent) : {};
    if (plainObject(parsed)) config = parsed;
  } catch (_) {
    // Invalid configuration disables the collector without affecting the storefront.
  }
  const configuredPage = plainObject(config.page) ? config.page : {};
  const page = {
    path: publicPath(configuredPage.path),
    title: configuredPage.type === "search" ? "Search" : boundedString(configuredPage.title, 300),
    type: PAGE_TYPES.has(configuredPage.type) ? configuredPage.type : "",
  };
  const enabled = config.enabled === true && Boolean(page.path && page.type)
    && !(window.Shopify && window.Shopify.designMode);
  const environment = config.environment === "production" ? "production" : "test";
  // Preview and live themes share an origin. Separate visits so preview campaigns
  // cannot enter production journeys; the older unscoped visit is never reused.
  const VISIT_KEY = environment === "production" ? PRODUCTION_VISIT_KEY : TEST_VISIT_KEY;
  const pageProduct = productPayload(config.product);
  const entryCampaign = campaignFrom(new URLSearchParams(window.location.search));
  const entryQuery = safeQueryFrom(new URLSearchParams(window.location.search));
  const immediateReferrer = parseReferrer();
  const handlers = [];
  const retryTimers = new Set();
  const requests = new Set();
  let active = false;
  let stateKnown = false;
  let consentEpoch = 0;
  let visitor = null;
  let visit = null;
  let viewId = "";
  let pageViewSent = false;
  let productViewSent = false;
  let cartViewSent = false;
  let viewClosed = false;
  let interval = null;
  let article = null;
  let readingVisible = false;
  let lastSampleAt = 0;
  let lastInteractionAt = 0;
  let engagedMilliseconds = 0;
  let maxScrollPercent = 0;
  let scrollMilestones = new Set();
  let intervalEvents = 0;
  let lastEngagement = "";
  let scrollScheduled = false;
  let lastCheckoutAt = -Infinity;

  function consentState() {
    if (!enabled || navigator.globalPrivacyControl === true) return false;
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== "function") return null;
    try { return privacy.analyticsProcessingAllowed() === true; } catch (_) { return null; }
  }

  function clearIdentifiers() {
    try { window.localStorage.removeItem(VISITOR_KEY); } catch (_) { /* Storage is optional. */ }
    for (const key of [TEST_VISIT_KEY, PRODUCTION_VISIT_KEY, UNSCOPED_VISIT_KEY, LEGACY_VISIT_KEY, CONTACT_EVENT_KEY]) {
      try { window.sessionStorage.removeItem(key); } catch (_) { /* Storage is optional. */ }
    }
    for (const name of [VISITOR_COOKIE, VISIT_COOKIE, VISIT_STARTED_COOKIE, VISIT_LANDING_COOKIE, ENVIRONMENT_COOKIE]) clearCookie(name);
  }

  function getVisitor() {
    const now = Date.now();
    try {
      const stored = JSON.parse(window.localStorage.getItem(VISITOR_KEY) || "null");
      if (plainObject(stored) && UUID.test(stored.id || "") && Number.isFinite(stored.expiresAt)
        && stored.expiresAt > now && stored.expiresAt <= now + VISITOR_LIFETIME) {
        writeCookie(VISITOR_COOKIE, stored.id, Math.floor(VISITOR_LIFETIME / 1000));
        return { id: stored.id };
      }
      const id = uuid();
      if (!id) return null;
      const record = JSON.stringify({ id, createdAt: now, expiresAt: now + VISITOR_LIFETIME });
      window.localStorage.setItem(VISITOR_KEY, record);
      // Some privacy modes silently discard writes; do not claim persistent identity there.
      if (window.localStorage.getItem(VISITOR_KEY) !== record) return null;
      writeCookie(VISITOR_COOKIE, id, Math.floor(VISITOR_LIFETIME / 1000));
      return { id };
    } catch (_) { return null; }
  }

  function saveVisit(value) {
    visit = value;
    try { window.sessionStorage.setItem(VISIT_KEY, JSON.stringify(value)); } catch (_) { /* Keep this page's in-memory visit. */ }
    writeCookie(VISIT_COOKIE, value.id, Math.floor(VISIT_LIFETIME / 1000));
    writeCookie(VISIT_STARTED_COOKIE, value.startedAt, Math.floor(VISIT_LIFETIME / 1000));
    writeCookie(VISIT_LANDING_COOKIE, value.landingPath, Math.floor(VISIT_LIFETIME / 1000));
    writeCookie(ENVIRONMENT_COOKIE, environment, Math.floor(VISIT_LIFETIME / 1000));
  }

  function validStoredVisit(value, now) {
    return plainObject(value) && UUID.test(value.id || "") && publicPath(value.landingPath)
      && typeof value.startedAt === "string" && Number.isFinite(Date.parse(value.startedAt))
      && Date.parse(value.startedAt) <= now && Number.isFinite(value.lastSeenAt)
      && value.lastSeenAt <= now && now - value.lastSeenAt < VISIT_LIFETIME
      && plainObject(value.campaign) && plainObject(value.query || {});
  }

  function newVisit(now) {
    const id = uuid();
    if (!id) return null;
    return {
      id, startedAt: new Date(now).toISOString(), landingPath: page.path,
      campaign: { ...entryCampaign }, referrer: immediateReferrer ? { ...immediateReferrer } : null,
      query: { ...entryQuery },
      lastSeenAt: now,
    };
  }

  function startVisit() {
    const now = Date.now();
    let stored = null;
    try { stored = JSON.parse(window.sessionStorage.getItem(VISIT_KEY) || "null"); } catch (_) { /* Use memory only. */ }
    const externalEntry = immediateReferrer && !sameSite(immediateReferrer.host, window.location.hostname);
    const campaignChanged = Object.keys(entryCampaign).length > 0
      && (!stored || campaignFingerprint(campaignFrom(stored.campaign)) !== campaignFingerprint(entryCampaign));
    if (validStoredVisit(stored, now) && !externalEntry && !campaignChanged) {
      saveVisit({
        id: stored.id, startedAt: stored.startedAt, landingPath: publicPath(stored.landingPath),
        campaign: campaignFrom(stored.campaign), referrer: cleanReferrer(stored.referrer), lastSeenAt: now,
        query: safeQueryFrom(stored.query),
      });
    } else {
      const created = newVisit(now);
      if (created) saveVisit(created);
    }
  }

  function refreshVisit() {
    const now = Date.now();
    if (!visit || now < visit.lastSeenAt || now - visit.lastSeenAt >= VISIT_LIFETIME) {
      const created = newVisit(now);
      if (!created) return false;
      saveVisit(created);
    } else {
      saveVisit({ ...visit, lastSeenAt: now });
    }
    return Boolean(visit && visit.id);
  }

  function clientPayload() {
    let timezone = "";
    try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch (_) { /* Optional. */ }
    const viewportDimension = (value) => Number.isFinite(value) ? Math.max(0, Math.min(20000, Math.round(value))) : 0;
    return {
      language: boundedString(navigator.language, 64), timezone: boundedString(timezone, 100),
      viewport: { width: viewportDimension(window.innerWidth), height: viewportDimension(window.innerHeight) },
    };
  }

  function cleanProperties(name, input) {
    if (!Object.prototype.hasOwnProperty.call(EVENT_PROPERTIES, name)) return null;
    const output = {};
    const value = plainObject(input) ? input : {};
    for (const key of EVENT_PROPERTIES[name]) {
      const raw = value[key];
      if (key === "destination_path" || key === "storefront_path") {
        const path = publicPath(raw);
        const productDestination = name === "product_card_click" || name === "add_to_cart_click";
        if (path && (!productDestination || /^\/products\/[a-zA-Z0-9][a-zA-Z0-9_-]*\/?$/.test(path))) output[key] = path;
      } else if (key === "destination_host") {
        if (host(raw)) output[key] = host(raw);
      } else if (key === "placement") {
        if (typeof raw === "string" && /^[a-zA-Z0-9_-]{1,80}$/.test(raw)) output[key] = raw;
      } else if (key === "selected_slug") {
        if (typeof raw === "string" && /^[a-z0-9][a-z0-9-]{0,99}$/.test(raw)) output[key] = raw;
      } else if (key === "selection") {
        if (raw === "other") output[key] = raw;
      } else if (key === "link_type") {
        if (raw === "internal" || raw === "external") output[key] = raw;
      } else if (key === "percent") {
        if ([25, 50, 75, 100].includes(raw)) output[key] = raw;
      } else if (key === "reason") {
        if (["interval", "hidden", "pagehide"].includes(raw)) output[key] = raw;
      } else if (Number.isInteger(raw)) {
        const bounds = {
          quantity: [1, 10000], item_count: [0, 10000],
          active_seconds: [0, MAX_ACTIVE_SECONDS], max_scroll_percent: [0, 100],
        }[key];
        if (bounds && raw >= bounds[0] && raw <= bounds[1]) output[key] = raw;
      }
    }
    const required = {
      link_click: ["destination_host", "placement", "link_type"], article_scroll: ["percent"],
      article_engagement: ["active_seconds", "max_scroll_percent", "reason"],
      cart_add_success: ["quantity"], cart_update: ["item_count"], checkout_click: ["placement"],
    }[name] || [];
    return required.every((key) => Object.prototype.hasOwnProperty.call(output, key)) ? output : null;
  }

  function cancelTransport() {
    retryTimers.forEach((timer) => window.clearTimeout(timer));
    retryTimers.clear();
    requests.forEach((controller) => controller.abort());
    requests.clear();
  }

  function post(body, attempt, onSuccess, epoch) {
    if (!active || consentState() !== true || epoch !== consentEpoch) return;
    const controller = new AbortController();
    requests.add(controller);
    try {
      Promise.resolve(window.fetch(ENDPOINT, {
        method: "POST", headers: { "Content-Type": "application/json" }, body,
        credentials: "omit", cache: "no-store", keepalive: true, signal: controller.signal,
      })).then((response) => {
        requests.delete(controller);
        if (!active || consentState() !== true || epoch !== consentEpoch) return;
        if (response.status === 204) {
          if (typeof onSuccess === "function") { try { onSuccess(); } catch (_) { /* Consumer errors never affect the page. */ } }
          return;
        }
        if (response.status !== 429 || attempt >= 2 || viewClosed) return;
        const retryAfter = Number(response.headers.get("Retry-After"));
        const delay = Number.isFinite(retryAfter) && retryAfter > 0
          ? Math.min(retryAfter * 1000, 300000) : Math.min(30000 * (2 ** attempt) + Math.round(Math.random() * 1000), 300000);
        const timer = window.setTimeout(() => {
          retryTimers.delete(timer);
          post(body, attempt + 1, onSuccess, epoch);
        }, delay);
        retryTimers.add(timer);
      }).catch(() => { requests.delete(controller); });
    } catch (_) {
      requests.delete(controller);
    }
  }

  function emit(name, options = {}) {
    if (!active || viewClosed || consentState() !== true || !viewId) return false;
    const properties = cleanProperties(name, options.properties);
    const eventId = UUID.test(options.eventId || "") ? options.eventId : uuid();
    if (properties === null || !eventId || !refreshVisit()) return false;
    const contact = name === "contact_form_submit";
    const product = contact ? null : productPayload(options.product);
    const payload = {
      schema_version: 2, source: "partforthat-storefront", environment,
      event_id: eventId, event_name: name, occurred_at: new Date().toISOString(),
      ...(visitor ? { visitor: { id: visitor.id } } : {}),
      visit: {
        id: visit.id, started_at: visit.startedAt, landing_path: visit.landingPath,
        ...(!contact && visit.referrer ? { referrer: { ...visit.referrer } } : {}),
        ...(!contact && Object.keys(visit.query || {}).length ? { query: { ...visit.query } } : {}),
      },
      page: { ...page, title: contact ? "Contact confirmation" : page.title, view_id: viewId },
      ...(!contact && immediateReferrer ? { referrer: { ...immediateReferrer } } : {}),
      ...(!contact && Object.keys(visit.campaign).length ? { campaign: { ...visit.campaign } } : {}),
      ...(!contact ? { client: clientPayload() } : {}),
      ...(product ? { product } : {}), properties,
    };
    const body = JSON.stringify(payload);
    if (new Blob([body]).size > 16 * 1024) return false;
    post(body, 0, options.onSuccess, consentEpoch);
    return true;
  }

  function sendPageEvents() {
    if (!pageViewSent) pageViewSent = emit("page_view", { product: pageProduct });
    if (page.type === "product" && pageProduct && !productViewSent) productViewSent = emit("view_product", { product: pageProduct });
    if (page.type === "cart" && !cartViewSent) cartViewSent = emit("cart_view");
  }

  function track(name, options = {}) {
    evaluateConsent();
    if (name === "page_view") {
      if (pageViewSent) return false;
      sendPageEvents();
      return pageViewSent;
    }
    if (name === "view_product" && productViewSent && page.type === "product") return false;
    if (name === "cart_view" && cartViewSent) return false;
    const sent = emit(name, plainObject(options) ? options : {});
    if (sent && name === "view_product" && page.type === "product") productViewSent = true;
    if (sent && name === "cart_view") cartViewSent = true;
    return sent;
  }

  function visibleAndFocused() {
    return document.visibilityState !== "hidden" && typeof document.hasFocus === "function" && document.hasFocus();
  }

  function monotonicNow() {
    return window.performance.now();
  }

  function findArticle() {
    // An <article> element can also wrap About-page copy. Only an actual Shopify
    // article route receives reading events, including the migrated native guides.
    article = null;
    if (page.type !== "article") return;
    const selector = boundedString(config.articleSelector, 500);
    try { article = selector ? document.querySelector(selector) : null; } catch (_) { article = null; }
    if (!article && page.type === "article") article = document.querySelector("#MainContent .rte");
  }

  function resetView() {
    viewId = uuid();
    pageViewSent = false;
    productViewSent = false;
    cartViewSent = false;
    viewClosed = false;
    engagedMilliseconds = 0;
    maxScrollPercent = 0;
    scrollMilestones = new Set();
    intervalEvents = 0;
    lastEngagement = "";
    lastCheckoutAt = -Infinity;
    lastSampleAt = monotonicNow();
    // Arrival grants at most one idle window; continued time requires interaction.
    lastInteractionAt = lastSampleAt;
    readingVisible = visibleAndFocused();
    findArticle();
  }

  function accrueReading(now = monotonicNow()) {
    if (active && article && readingVisible && !viewClosed) {
      const through = Math.min(now, lastInteractionAt + READING_IDLE);
      engagedMilliseconds = Math.min(MAX_ACTIVE_SECONDS * 1000,
        engagedMilliseconds + Math.max(0, through - lastSampleAt));
    }
    lastSampleAt = now;
  }

  function flushEngagement(reason) {
    if (!active || !article || viewClosed) return;
    accrueReading();
    const seconds = Math.floor(engagedMilliseconds / 1000);
    const snapshot = `${seconds}:${maxScrollPercent}`;
    if ((!seconds && !maxScrollPercent) || snapshot === lastEngagement) return;
    if (reason === "interval" && intervalEvents >= MAX_INTERVAL_EVENTS) return;
    if (emit("article_engagement", { properties: { active_seconds: seconds, max_scroll_percent: maxScrollPercent, reason } })) {
      lastEngagement = snapshot;
      if (reason === "interval") intervalEvents += 1;
    }
  }

  function sampleScroll() {
    scrollScheduled = false;
    if (!active || !article || viewClosed || !visibleAndFocused()) return;
    const bounds = article.getBoundingClientRect();
    const height = bounds.height || article.offsetHeight;
    if (!(height > 0) || bounds.top >= window.innerHeight) return;
    // Progress is through the article body, excluding footer/comments. Sample only after
    // actual scrolling: an initially short article never automatically becomes 100% read.
    const progress = Math.max(0, Math.min(100, Math.floor(((window.innerHeight - bounds.top) / height) * 100)));
    maxScrollPercent = Math.max(maxScrollPercent, progress);
    for (const milestone of [25, 50, 75, 100]) {
      if (progress >= milestone && !scrollMilestones.has(milestone)) {
        if (emit("article_scroll", { properties: { percent: milestone } })) scrollMilestones.add(milestone);
      }
    }
  }

  function interaction(event) {
    evaluateConsent();
    if (!active || viewClosed) return;
    const now = monotonicNow();
    accrueReading(now);
    lastInteractionAt = now;
    readingVisible = visibleAndFocused();
    // Visible real activity keeps a visit alive even after milestone/heartbeat
    // limits are reached. Throttle persistence so scrolling does not thrash storage.
    if (readingVisible && (!visit || Date.now() - visit.lastSeenAt >= READING_INTERVAL)) refreshVisit();
    if (event.type === "scroll" && article && !scrollScheduled) {
      scrollScheduled = true;
      window.requestAnimationFrame(sampleScroll);
    }
  }

  function startInterval() {
    if (interval !== null) return;
    interval = window.setInterval(() => {
      evaluateConsent();
      if (active && !viewClosed) flushEngagement("interval");
    }, READING_INTERVAL);
  }

  function stopInterval() {
    if (interval !== null) window.clearInterval(interval);
    interval = null;
  }

  function notifyState() {
    handlers.forEach((handler) => { try { handler(active); } catch (_) { /* Isolate consumer errors. */ } });
  }

  function evaluateConsent() {
    const allowed = consentState();
    if (allowed === null) {
      // An API becoming unavailable after consent is not permission to keep collecting.
      if (!active) return;
    } else if (stateKnown && allowed === active) return;
    stateKnown = allowed !== null;
    active = allowed === true;
    consentEpoch += 1;
    if (active) {
      visitor = getVisitor();
      startVisit();
      resetView();
      sendPageEvents();
      startInterval();
    } else {
      cancelTransport();
      stopInterval();
      visitor = null;
      visit = null;
      readingVisible = false;
      engagedMilliseconds = 0;
      maxScrollPercent = 0;
      clearIdentifiers();
    }
    notifyState();
  }

  window.PartForThatAnalytics = {
    sitewide: true, track,
    onStateChange(handler) {
      if (typeof handler !== "function") return;
      handlers.push(handler);
      if (stateKnown || !enabled) { try { handler(active); } catch (_) { /* Isolate consumer errors. */ } }
    },
    getVisitId: () => active && consentState() === true && visit ? visit.id : "",
    getCampaign: () => active && consentState() === true && visit ? { ...visit.campaign } : {},
    getLandingPath: () => active && consentState() === true && visit ? visit.landingPath : "",
  };

  function placement(element) {
    if (!element || typeof element.closest !== "function") return "main";
    const tagged = element.closest("[data-pft-analytics-placement]");
    const specified = tagged && tagged.getAttribute("data-pft-analytics-placement");
    if (specified && /^[a-zA-Z0-9_-]{1,80}$/.test(specified)) return specified;
    if (element.closest("cart-drawer, .cart-drawer, [data-cart-drawer]")) return "cart_drawer";
    if (element.closest("header")) return "header";
    if (element.closest("footer")) return "footer";
    if (article && article.contains(element)) return "article";
    if (page.type === "cart") return "cart";
    if (element.closest("nav")) return "navigation";
    return "main";
  }

  function checkoutIntent(element) {
    if (!active || viewClosed) return;
    const now = monotonicNow();
    if (now - lastCheckoutAt < 1000) return;
    linkCartToVisit();
    if (track("checkout_click", { properties: { placement: placement(element) } })) lastCheckoutAt = now;
  }

  function linkCartToVisit() {
    if (!active || consentState() !== true || !visit || !UUID.test(visit.id)) return;
    try {
      if (window.sessionStorage.getItem(CART_LINK_KEY) === visit.id) return;
      window.sessionStorage.setItem(CART_LINK_KEY, visit.id);
    } catch (_) { /* A duplicate link request is safe. */ }
    try {
      window.fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ attributes: { pft_visit_id__: visit.id, pft_environment__: environment } }),
        credentials: "same-origin", cache: "no-store", keepalive: true,
      }).catch(() => {});
    } catch (_) { /* Cart attribution must never affect checkout. */ }
  }

  function click(event) {
    if (event.defaultPrevented || (typeof event.button === "number" && event.button !== 0)) return;
    const target = event.target && typeof event.target.closest === "function" ? event.target : event.target && event.target.parentElement;
    if (!target || typeof target.closest !== "function") return;
    const checkout = target.closest('button[name="checkout"], input[name="checkout"], [data-pft-checkout]');
    if (checkout) { checkoutIntent(checkout); return; }
    const anchor = target.closest("a[href]");
    if (!anchor) return;
    // The landing adapter owns these more specific click events.
    if (anchor.matches("[data-pfts-product-link], [data-pfts-shop-all]")) return;
    const rawHref = anchor.getAttribute("href") || "";
    if (!rawHref || rawHref.startsWith("#")) return;
    let destination;
    try { destination = new URL(anchor.href || rawHref, window.location.href); } catch (_) { return; }
    if (!/^https?:$/.test(destination.protocol) || destination.username || destination.password) return;
    const hostname = host(destination.hostname);
    if (!hostname) return;
    const internal = sameSite(hostname, window.location.hostname);
    if (internal && /^\/checkout(?:\/|$)/.test(destination.pathname)) { checkoutIntent(anchor); return; }
    if (/^\/(?:account|accounts|checkout|checkouts)(?:\/|$)/.test(destination.pathname)) return;
    const path = internal ? publicPath(destination.pathname) : "";
    if (internal && !path) return;
    track("link_click", {
      properties: {
        ...(path ? { destination_path: path } : {}), destination_host: hostname,
        placement: placement(anchor), link_type: internal ? "internal" : "external",
      },
    });
  }

  function formSubmission(event) {
    const form = event.target;
    if (!form || typeof form.getAttribute !== "function") return;
    const submitter = event.submitter;
    if (submitter && submitter.name === "checkout") { checkoutIntent(submitter); return; }
    try {
      const destination = new URL(form.getAttribute("action") || "", window.location.href);
      if (sameSite(destination.hostname, window.location.hostname) && /^\/checkout(?:\/|$)/.test(destination.pathname)) checkoutIntent(form);
    } catch (_) { /* Invalid form actions have no analytics meaning. */ }
  }

  function cartUpdated(event) {
    const detail = plainObject(event.detail) ? event.detail : {};
    const data = plainObject(detail.data) ? detail.data : {};
    if (data.didError) return;
    if (data.source === "product-form-component") {
      linkCartToVisit();
      const quantity = Number.isInteger(data.quantity) ? data.quantity : data.itemCount;
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 10000) return;
      const explicitVariant = String(data.variantId || detail.sourceId || "");
      // Horizon batches report a total quantity, not a reliable single variant.
      // Include product details only when the explicit variant actually matches.
      const product = pageProduct && explicitVariant === pageProduct.variant_id ? pageProduct : null;
      track("cart_add_success", { product, properties: { quantity } });
    } else if (data.source === "cart-items-component") {
      linkCartToVisit();
      const resource = plainObject(detail.resource) ? detail.resource : {};
      const count = Number.isInteger(resource.item_count) ? resource.item_count : data.itemCount;
      if (Number.isInteger(count) && count >= 0 && count <= 10000) track("cart_update", { properties: { item_count: count } });
    }
  }

  if (!enabled) return;
  document.addEventListener("visitorConsentCollected", evaluateConsent);
  document.addEventListener("click", click);
  document.addEventListener("submit", formSubmission, true);
  document.addEventListener("cart:update", cartUpdated);
  for (const name of ["pointerdown", "keydown", "touchstart", "scroll"]) {
    document.addEventListener(name, interaction, { passive: true, capture: name === "scroll" });
  }
  document.addEventListener("visibilitychange", () => {
    if (!active) { evaluateConsent(); return; }
    if (document.visibilityState === "hidden") flushEngagement("hidden");
    else accrueReading();
    readingVisible = visibleAndFocused();
  });
  window.addEventListener("blur", () => {
    if (!active) return;
    flushEngagement("hidden");
    readingVisible = false;
  });
  window.addEventListener("focus", () => {
    evaluateConsent();
    if (!active) return;
    accrueReading();
    lastInteractionAt = monotonicNow();
    readingVisible = visibleAndFocused();
  });
  window.addEventListener("pagehide", () => {
    flushEngagement("pagehide");
    viewClosed = true;
    stopInterval();
    // Keep the final keepalive request alive, but never schedule stale retries.
    retryTimers.forEach((timer) => window.clearTimeout(timer));
    retryTimers.clear();
  });
  window.addEventListener("pageshow", (event) => {
    if (event.persisted && viewClosed && active && consentState() === true) {
      resetView();
      sendPageEvents();
      startInterval();
    } else {
      evaluateConsent();
    }
  });

  evaluateConsent();
  if (navigator.globalPrivacyControl !== true && consentState() === null
    && window.Shopify && typeof window.Shopify.loadFeatures === "function") {
    try {
      window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], evaluateConsent);
    } catch (_) { /* Remain disabled until an affirmative consent state is available. */ }
  }
})();
